import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Loader2, Copy, Clock, CheckCircle, XCircle, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PlaygroundProps {
  apiKeys: { id: string; name: string; key_prefix: string }[];
}

interface ApiResponse {
  status: number;
  statusText: string;
  data: any;
  responseTime: number;
  headers: Record<string, string>;
}

const API_BASE_URL = 'https://ateoqefwjeivvlnfpmon.supabase.co/functions/v1/api-handler';

const ENDPOINTS = [
  // GET endpoints
  { value: '/forms', label: 'List all forms', method: 'GET', category: 'read' },
  { value: '/forms/:id', label: 'Get specific form', method: 'GET', category: 'read' },
  { value: '/forms/:id/submissions', label: 'Get form submissions', method: 'GET', category: 'read' },
  { value: '/submissions', label: 'List all submissions', method: 'GET', category: 'read' },
  { value: '/contacts', label: 'List all contacts', method: 'GET', category: 'read' },
  { value: '/analytics', label: 'Get analytics data', method: 'GET', category: 'read' },
  // POST endpoints
  { value: '/forms', label: 'Create new form', method: 'POST', category: 'write' },
  { value: '/forms/:id/submissions', label: 'Create submission', method: 'POST', category: 'write' },
  { value: '/contacts', label: 'Create contact', method: 'POST', category: 'write' },
  // PUT endpoints
  { value: '/forms/:id', label: 'Update form', method: 'PUT', category: 'write' },
  { value: '/contacts/:id', label: 'Update contact', method: 'PUT', category: 'write' },
  // DELETE endpoints
  { value: '/forms/:id', label: 'Delete form', method: 'DELETE', category: 'write' },
  { value: '/submissions/:id', label: 'Delete submission', method: 'DELETE', category: 'write' },
  { value: '/contacts/:id', label: 'Delete contact', method: 'DELETE', category: 'write' },
];

const SAMPLE_BODIES = {
  'POST /forms': JSON.stringify({
    title: "Contact Form",
    description: "A simple contact form",
    fields: [
      { id: "1", type: "text", label: "Name", required: true },
      { id: "2", type: "email", label: "Email", required: true }
    ],
    is_published: true
  }, null, 2),
  'POST /forms/:id/submissions': JSON.stringify({
    name: "John Doe",
    email: "john@example.com",
    message: "Hello, this is a test submission!"
  }, null, 2),
  'POST /contacts': JSON.stringify({
    name: "Jane Doe",
    email: "jane@example.com",
    phone: "+2348012345678"
  }, null, 2),
  'PUT /forms/:id': JSON.stringify({
    title: "Updated Form Title",
    description: "Updated description"
  }, null, 2),
  'PUT /contacts/:id': JSON.stringify({
    name: "Updated Name",
    notes: "VIP customer"
  }, null, 2),
};

export const APIPlayground = ({ apiKeys }: PlaygroundProps) => {
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<'GET' | 'POST' | 'PUT' | 'DELETE'>('GET');
  const [selectedEndpoint, setSelectedEndpoint] = useState('/forms');
  const [customPath, setCustomPath] = useState('');
  const [queryParams, setQueryParams] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<ApiResponse | null>(null);

  const filteredEndpoints = ENDPOINTS.filter(e => e.method === selectedMethod);

  const handleMethodChange = (method: 'GET' | 'POST' | 'PUT' | 'DELETE') => {
    setSelectedMethod(method);
    const firstEndpoint = ENDPOINTS.find(e => e.method === method);
    if (firstEndpoint) {
      setSelectedEndpoint(firstEndpoint.value);
      // Set sample body for write operations
      const bodyKey = `${method} ${firstEndpoint.value}` as keyof typeof SAMPLE_BODIES;
      setRequestBody(SAMPLE_BODIES[bodyKey] || '');
    }
    setResponse(null);
  };

  const handleEndpointChange = (endpoint: string) => {
    setSelectedEndpoint(endpoint);
    const bodyKey = `${selectedMethod} ${endpoint}` as keyof typeof SAMPLE_BODIES;
    setRequestBody(SAMPLE_BODIES[bodyKey] || '');
  };

  const handleTest = async () => {
    if (!apiKey.trim()) {
      toast({
        title: "API Key Required",
        description: "Please enter your API key to test the endpoint",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setResponse(null);
    const startTime = performance.now();

    try {
      // Build the URL
      const endpoint = customPath || selectedEndpoint;
      let url = `${API_BASE_URL}${endpoint}`;
      
      if (queryParams.trim() && selectedMethod === 'GET') {
        const params = queryParams.split('\n')
          .filter(line => line.includes('='))
          .map(line => {
            const [key, ...valueParts] = line.split('=');
            return `${encodeURIComponent(key.trim())}=${encodeURIComponent(valueParts.join('=').trim())}`;
          })
          .join('&');
        if (params) {
          url += `?${params}`;
        }
      }

      const fetchOptions: RequestInit = {
        method: selectedMethod,
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      };

      // Add body for POST/PUT requests
      if ((selectedMethod === 'POST' || selectedMethod === 'PUT') && requestBody.trim()) {
        try {
          // Validate JSON
          JSON.parse(requestBody);
          fetchOptions.body = requestBody;
        } catch {
          toast({
            title: "Invalid JSON",
            description: "Request body must be valid JSON",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
      }

      const fetchResponse = await fetch(url, fetchOptions);

      const responseTime = Math.round(performance.now() - startTime);
      const data = await fetchResponse.json();

      // Extract relevant headers
      const headers: Record<string, string> = {};
      fetchResponse.headers.forEach((value, key) => {
        if (['content-type', 'x-request-id', 'date'].includes(key.toLowerCase())) {
          headers[key] = value;
        }
      });

      setResponse({
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        data,
        responseTime,
        headers
      });

    } catch (error: any) {
      const responseTime = Math.round(performance.now() - startTime);
      setResponse({
        status: 0,
        statusText: 'Network Error',
        data: { error: error.message },
        responseTime,
        headers: {}
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(JSON.stringify(response.data, null, 2));
      toast({
        title: "Copied",
        description: "Response copied to clipboard"
      });
    }
  };

  const copyCurl = () => {
    const endpoint = customPath || selectedEndpoint;
    const url = `${API_BASE_URL}${endpoint}`;
    
    let curl = `curl -X ${selectedMethod} "${url}" \\\n  -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\\n  -H "Content-Type: application/json"`;
    
    if ((selectedMethod === 'POST' || selectedMethod === 'PUT') && requestBody.trim()) {
      curl += ` \\\n  -d '${requestBody.replace(/\n/g, '')}'`;
    }

    navigator.clipboard.writeText(curl);
    toast({
      title: "Copied",
      description: "cURL command copied to clipboard"
    });
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'bg-green-500';
    if (status >= 400 && status < 500) return 'bg-yellow-500';
    if (status >= 500) return 'bg-red-500';
    return 'bg-gray-500';
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-blue-500';
      case 'POST': return 'bg-green-500';
      case 'PUT': return 'bg-orange-500';
      case 'DELETE': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Card className="card-elegant">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="h-5 w-5 text-primary" />
          API Playground
        </CardTitle>
        <CardDescription>
          Test your API keys and endpoints with live requests (GET, POST, PUT, DELETE)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* API Key Input */}
        <div className="space-y-2">
          <Label>API Key</Label>
          <Input
            type="password"
            placeholder="Enter your full API key (wf_live_sk_...)"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="font-mono"
          />
          {apiKeys.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Your keys: {apiKeys.map(k => k.key_prefix).join(', ')}
            </p>
          )}
        </div>

        {/* Method Selection */}
        <div className="space-y-2">
          <Label>HTTP Method</Label>
          <Tabs value={selectedMethod} onValueChange={(v) => handleMethodChange(v as any)}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="GET" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                GET
              </TabsTrigger>
              <TabsTrigger value="POST" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
                POST
              </TabsTrigger>
              <TabsTrigger value="PUT" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                PUT
              </TabsTrigger>
              <TabsTrigger value="DELETE" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
                DELETE
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Endpoint Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Endpoint</Label>
            <Select value={selectedEndpoint} onValueChange={handleEndpointChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select endpoint" />
              </SelectTrigger>
              <SelectContent>
                {filteredEndpoints.map(endpoint => (
                  <SelectItem key={`${endpoint.method}-${endpoint.value}`} value={endpoint.value}>
                    <div className="flex items-center gap-2">
                      <Badge className={`${getMethodColor(endpoint.method)} text-white text-xs`}>
                        {endpoint.method}
                      </Badge>
                      <span>{endpoint.value}</span>
                      <span className="text-muted-foreground text-xs">- {endpoint.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Custom Path (optional)</Label>
            <Input
              placeholder="/forms/uuid-here"
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
              className="font-mono"
            />
          </div>
        </div>

        {/* Query Parameters (for GET) */}
        {selectedMethod === 'GET' && (
          <div className="space-y-2">
            <Label>Query Parameters (one per line: key=value)</Label>
            <Textarea
              placeholder="limit=10&#10;offset=0&#10;form_id=..."
              value={queryParams}
              onChange={(e) => setQueryParams(e.target.value)}
              rows={3}
              className="font-mono text-sm"
            />
          </div>
        )}

        {/* Request Body (for POST/PUT) */}
        {(selectedMethod === 'POST' || selectedMethod === 'PUT') && (
          <div className="space-y-2">
            <Label>Request Body (JSON)</Label>
            <Textarea
              placeholder='{"key": "value"}'
              value={requestBody}
              onChange={(e) => setRequestBody(e.target.value)}
              rows={8}
              className="font-mono text-sm"
            />
          </div>
        )}

        {/* Request URL Preview */}
        <div className="p-3 rounded-lg bg-muted/50 border">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`${getMethodColor(selectedMethod)} text-white`}>
              {selectedMethod}
            </Badge>
            <p className="text-xs text-muted-foreground">Request URL</p>
          </div>
          <code className="text-sm break-all">
            {API_BASE_URL}{customPath || selectedEndpoint}
            {queryParams && selectedMethod === 'GET' && '?...'}
          </code>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button onClick={handleTest} disabled={isLoading} className="flex-1">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Request
              </>
            )}
          </Button>
          <Button variant="outline" onClick={copyCurl}>
            <Copy className="h-4 w-4 mr-2" />
            Copy cURL
          </Button>
        </div>

        {/* Response */}
        {response && (
          <div className="space-y-4 border-t pt-4">
            {/* Response Status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className={`${getStatusColor(response.status)} text-white`}>
                  {response.status || 'ERR'} {response.statusText}
                </Badge>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {response.responseTime}ms
                </div>
                {response.status >= 200 && response.status < 300 ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={copyResponse}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>

            {/* Response Headers */}
            {Object.keys(response.headers).length > 0 && (
              <div className="text-xs">
                <p className="font-medium text-muted-foreground mb-1">Headers</p>
                <div className="bg-muted/50 rounded p-2 font-mono">
                  {Object.entries(response.headers).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-primary">{key}:</span> {value}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Response Body */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Response Body</p>
              <div className="bg-muted/50 rounded-lg p-4 overflow-auto max-h-96">
                <pre className="text-sm font-mono whitespace-pre-wrap">
                  {JSON.stringify(response.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};